"use client";

import { useId } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Card from "@churchofjesuschrist/eden-card";
import { Clear } from "@churchofjesuschrist/eden-icons";
import { accentColorInfo, boxShadowRaised, borderColorTertiary, spacing4, spacing8, spacing16, spacing24, spacing32, backgroundColorLevel5, spacing48 } from "@churchofjesuschrist/eden-style-constants";
import { A11y as EdenButton } from "@churchofjesuschrist/eden-buttons";
import { H4 } from "@churchofjesuschrist/eden-headings";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const barWidth = spacing8;
const borderRadius = "2px";
const space = spacing8;
const AlertCard = styled(Card).withConfig({
  componentId: "sc-pay24v-0"
})(["box-shadow:", ";border:1px solid ", ";border-radius:", ";background-color:", ";"], boxShadowRaised, borderColorTertiary, borderRadius, backgroundColorLevel5);

// Eden rules state an outer wrapper can not have `position:relative`, so this needs to be put on a child. The `flex` needs to be a direct parent of the icon and content so this wrapper gets that too. Documenting just so someone doesn't look at this in the future and try to combine them (AlertCard and FlexPositionWrapper)
const FlexPositionWrapper = styled.div.withConfig({
  componentId: "sc-pay24v-1"
})(["padding:", ";padding-inline-end:", ";display:flex;position:relative;&:before{position:absolute;content:\"\";display:block;width:", ";height:calc(100% + 2px);top:-1px;inset-inline-start:-1px;background:", ";border-start-start-radius:", ";border-end-start-radius:", ";}"], spacing16, spacing48, barWidth, ({
  $borderColor
}) => $borderColor, borderRadius, borderRadius);
const ContentWrapper = styled.div.withConfig({
  componentId: "sc-pay24v-2"
})(["width:100%;z-index:1;display:flex;align-items:flex-start;gap:", ";"], space);
const IconWrapper = styled.div.withConfig({
  componentId: "sc-pay24v-3"
})(["display:flex;"]);
const Content = styled.div.withConfig({
  componentId: "sc-pay24v-4"
})(["flex:1;"]);
const Close = styled.div.withConfig({
  componentId: "sc-pay24v-5"
})(["display:flex;"]);
const Button = styled(EdenButton).withConfig({
  componentId: "sc-pay24v-6"
})(["max-height:", ";position:absolute;top:", ";inset-inline-end:", ";&:before{position:absolute;content:\"\";display:block;width:", ";height:", ";top:-", ";left:-", ";}"], spacing24, spacing16, spacing16, spacing32, spacing32, spacing4, spacing4);
const Alert = ({
  children,
  onClose,
  Icon,
  title,
  renderTitleAs = "h2",
  color = accentColorInfo,
  closeLabel = "Close",
  ...props
}) => {
  const titleId = useId();
  const labelledBy = title ? titleId : null;
  return /*#__PURE__*/_jsx(AlertCard, {
    "aria-labelledby": labelledBy,
    ...props,
    children: /*#__PURE__*/_jsxs(FlexPositionWrapper, {
      $borderColor: color,
      children: [/*#__PURE__*/_jsxs(ContentWrapper, {
        children: [/*#__PURE__*/_jsx(IconWrapper, {
          children: Icon && /*#__PURE__*/_jsx(Icon, {
            size: spacing24,
            color: color
          })
        }), /*#__PURE__*/_jsxs(Content, {
          children: [title && /*#__PURE__*/_jsx(H4, {
            renderAs: renderTitleAs,
            id: titleId,
            children: title
          }), children]
        })]
      }), onClose && /*#__PURE__*/_jsx(Close, {
        children: /*#__PURE__*/_jsx(Button, {
          title: closeLabel,
          size: "small",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(Clear, {
            size: spacing24
          })
        })
      })]
    })
  });
};
Alert.displayName = "Alert";
Alert.propTypes = {
  /** Content inside the alert */
  children: PropTypes.node,
  /** Callback function to execute when user dismisses the alert */
  onClose: PropTypes.func,
  /** The icon component to use. */
  Icon: PropTypes.func,
  /** Optional title for the content inside the alert. Will be rendered as an \<H2\>, \<H3\>, \<H4\>, \<H5\>, or \<H6\>. */
  title: PropTypes.string,
  /** The title will be rendered at the selected level. */
  renderTitleAs: PropTypes.oneOf(["h2", "h3", "h4", "h5", "h6"]),
  /** The main color value to apply. */
  color: PropTypes.string,
  /** Label for the "close" control. */
  closeLabel: PropTypes.string
};
export default Alert;